/**
 * Stand-ins for the parts of the wearer that a real pair of glasses disappears
 * behind, drawn into the depth buffer only.
 *
 * The video is a flat backdrop, so nothing stops the temples being painted
 * straight over the cheeks — they read as sitting *on* the face rather than
 * running behind it to the ears. Giving the wearer some depth lets the GPU
 * sort that out: geometry behind these volumes is discarded, and the near
 * temple reappears by itself as the head turns. No yaw thresholds, no fading.
 *
 * Numbers are in frame widths, anchored to the frame's *front plane* rather
 * than to the head. That is what guarantees the frame can never occlude
 * itself, however far auto-fit pushes it back: the whole volume moves with it,
 * keeping the same clearance.
 *
 * The head alone is not enough. It ends at z=-0.68 while the temples run back
 * to between -1.02 and -1.23, so the tips — the part that in life hooks down
 * behind the ear — hang past it in mid-air. Head-on they are hidden anyway,
 * but from about 15 degrees of yaw they swing clear of the head's silhouette
 * and float over the picture. Widening or deepening the head cannot reach
 * them: an ellipsoid is at its widest only near its own centre, and
 * glasses-1's tips run dead straight back at |x|=0.49 without ever curving
 * inward. What hides a temple tip on a real face is the ear, so that is what
 * these are — small volumes standing slightly proud of the temples, at the
 * depth the tips occupy.
 *
 * Sizes were picked by ray-testing all three GLBs against candidate volumes
 * across a sweep of head angles; run `node scripts/tune-occluder.mjs` to
 * reproduce the numbers, or to re-tune after adding a frame. As it stands the
 * tips are 100% hidden from -60 to +90 degrees of yaw, the temple bodies stay
 * visible bar the sliver right at the ear, and nothing ever touches the frame
 * front.
 */
export const OCCLUDERS = [
  /**
   * The head.
   *
   *   half-width 0.50   the face width we measure, by definition
   *   half-height 0.70  chin to well above the brow, centred a little low
   *   depth 0.28        the front pole ends up 0.12 behind the frame, which
   *                     clears the deepest frame-front geometry in any of the
   *                     models (0.066) with room to spare, while still cutting
   *                     in ahead of where the temples begin
   *
   * Widening this to reach glasses-1's temples, which run right along a 0.50
   * surface, is a dead end worth not repeating: it raises the share of temple
   * *vertices* hidden head-on but clears no pixels at all, because those
   * vertices are already behind the frame's own opaque front. The visible part
   * of that temple starts at z=-0.059, ahead of this front pole, and cannot be
   * occluded without eating the lens with it.
   */
  { radii: [0.5, 0.7, 0.28], centre: [0, -0.15, -0.4] },
  /**
   * The ears. Set at |x|=0.48 with a half-width of 0.16, so they stand proud
   * of temples running at |x|=0.49 and are therefore nearer the camera than
   * the tip from any angle that shows that side of the head. The z span,
   * -1.32 to -0.72, covers every model's tip while starting behind where the
   * temple bodies want to stay visible.
   */
  { radii: [0.16, 0.3, 0.3], centre: [-0.48, -0.05, -1.02] },
  { radii: [0.16, 0.3, 0.3], centre: [0.48, -0.05, -1.02] },
];
